import * as React from 'react';
import styled from 'styled-components';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import FieldDecorator from './FieldDecorator';
import { ChangeableValue } from './Input';
import { customiseErrorMessageForKendoField, hasValue, setFormFieldErrorMessage } from '../../lib/utils';
import './fieldStyles.less';
import Option from '../../models/Option';
import { blueyGrey, fieldBackgroundWhite } from '../../appComponents/cssConstants';

const DropDownListField = (props: Props) => {
	const {
		fieldId,
		label,
		title,
		className,
		textField,
		dataItemKey,
		data,
		value,
		onValueChange,
		validateOnMount = true,
		errorMessage = null,
		isValueFromDataKey = false,
		...rest
	} = props;

	let textFieldDefaulted = textField;
	let dataItemKeyDefaulted = dataItemKey;

	if (!textField && !dataItemKey && data && data.length > 0) {
		if ((data[0] as Option<unknown>).text !== undefined && (data[0] as Option<unknown>).value !== undefined) {
			textFieldDefaulted = 'text';
			dataItemKeyDefaulted = 'value';
		}
	}

	React.useEffect(() => {
		// In order to allow a parent to know whether the initial state of an input is valid or not, we fire the
		// onValueChange code.
		if (validateOnMount) {
			const updatedErrorMessage = props.required && !hasValue(props.value) ? `${label} is required.` : null;
			onValueChange(props.name, props.value as string | number | boolean, updatedErrorMessage);
		}
	}, []);

	const handleChange = (event: DropDownListChangeEvent) => {
		setFormFieldErrorMessage(event.target.element);
		const { value: updatedValue } = event.target;
		onValueChange(props.name, isValueFromDataKey ? updatedValue[dataItemKeyDefaulted] : updatedValue, null);
	};

	const waitDomRenderInMilliSeconds = 1000;
	setTimeout(customiseErrorMessageForKendoField, waitDomRenderInMilliSeconds);

	const actualValue =
		data && data[0] && (data[0] as Option<unknown>).value && value && !(value as Option<unknown>).value
			? (data as Option<unknown>[]).find((x) => (x.value as Option<unknown>) === value)
			: value;

	return (
		<FieldDecorator
			id={fieldId ?? props.name}
			label={label}
			title={title}
			className={className}
			errorMessage={errorMessage}
		>
			<DropDownListWrapper width={props.width}>
				<DropDownList
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...rest}
					textField={textFieldDefaulted}
					dataItemKey={dataItemKeyDefaulted}
					onChange={handleChange}
					value={actualValue}
					data={data}
					popupSettings={{ className }}
				/>
			</DropDownListWrapper>
		</FieldDecorator>
	);
};

interface Props extends ChangeableValue {
	name: string;
	data: string[] | Option<string>[] | Option<number>[];
	value: number | string | Option<string> | Option<number>;
	fieldId?: string;
	label?: string;
	title?: string;
	className?: string;
	textField?: string;
	dataItemKey?: string;
	defaultValue?: number | string | Option<string> | Option<number>;
	required?: boolean;
	validateOnMount?: boolean;
	errorMessage?: string;
	isValueFromDataKey?: boolean;
	disabled?: boolean;
	defaultItem?: unknown;
	width?: string;
}

DropDownListField.defaultProps = {
	validateOnMount: true,
	errorMessage: null,
	isValueFromDataKey: false,
	fieldId: null,
	label: null,
	title: null,
	className: null,
	textField: null,
	dataItemKey: null,
	defaultValue: null,
	required: false,
	disabled: false,
	defaultItem: null,
	width: null
};

export default DropDownListField;

export const DropDownListWrapper = styled.div<{ width: string }>`
	${(props) => (hasValue(props.width) ? `width: ${props.width};` : '')}
	.k-dropdown {
		display: flex;
		border-radius: 8px;
		border-color: ${blueyGrey};
	}

	.k-dropdown-wrap {
		.k-clear-value {
			top: 6px;
		}

		.k-input {
			background: ${fieldBackgroundWhite};
			border-radius: 8px;
			font-size: 18px;
			font-weight: normal;
		}
	}
`;
